/**
 * Bank Guarantee instruments.
 *
 * One project may have multiple BG instruments over its lifetime (renewal,
 * replacement, encashment-and-reissue). Status is derived from the recorded
 * dates plus the `replacesBgId` chain:
 *
 *   - Released:  released_date set
 *   - Encashed:  applied_date set, not released
 *   - Accepted:  accepted_date set, not encashed, not released
 *   - Active:    submitted_date set, none of the above
 *   - Replaced:  another instrument's replaces_bg_id points to this row
 */

import { Type, type Static } from '@sinclair/typebox';
import { BankGuaranteeStatuses } from '../enums.js';
import { FIELD_LIMITS } from '../constants.js';
import {
  IsoDateSchema,
  PaisaSchema,
  RowVersionField,
  SoftDeleteFields,
  TimestampFields,
  UuidSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Read shape — stored fields
// ---------------------------------------------------------------------------

export const BankGuaranteeSchema = Type.Object({
  id: UuidSchema,
  projectId: UuidSchema,

  amountPaisa: PaisaSchema,

  submittedDate: Type.Union([IsoDateSchema, Type.Null()]),
  acceptedDate: Type.Union([IsoDateSchema, Type.Null()]),
  appliedDate: Type.Union([IsoDateSchema, Type.Null()]),
  releasedDate: Type.Union([IsoDateSchema, Type.Null()]),

  /** Points back to the previous instrument this one replaces, if any. */
  replacesBgId: Type.Union([UuidSchema, Type.Null()]),

  notes: Type.Union([Type.String({ maxLength: FIELD_LIMITS.BG_NOTES_MAX }), Type.Null()]),

  createdByUserId: UuidSchema,

  ...TimestampFields,
  ...SoftDeleteFields,
  ...RowVersionField,
});
export type BankGuarantee = Static<typeof BankGuaranteeSchema>;

// ---------------------------------------------------------------------------
// Read shape with derived status
// ---------------------------------------------------------------------------

export const BankGuaranteeWithStatusSchema = Type.Composite([
  BankGuaranteeSchema,
  Type.Object({
    status: Type.Union(BankGuaranteeStatuses.map((s) => Type.Literal(s))),
  }),
]);
export type BankGuaranteeWithStatus = Static<typeof BankGuaranteeWithStatusSchema>;

// ---------------------------------------------------------------------------
// Create / update inputs
// ---------------------------------------------------------------------------

/**
 * Create — Admin only. Used both for the first instrument and for renewals.
 * If `replacesBgId` is set, the referenced instrument is implicitly closed
 * (its derived status will become REPLACED).
 */
export const BankGuaranteeCreateSchema = Type.Object({
  amountPaisa: PaisaSchema,
  submittedDate: Type.Optional(IsoDateSchema),
  replacesBgId: Type.Optional(UuidSchema),
  notes: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.BG_NOTES_MAX })),
});
export type BankGuaranteeCreate = Static<typeof BankGuaranteeCreateSchema>;

/**
 * Update — both Admin and Assistant can advance dates on an existing instrument.
 */
export const BankGuaranteeUpdateSchema = Type.Object({
  amountPaisa: Type.Optional(PaisaSchema),
  submittedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  acceptedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  appliedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  releasedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  notes: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.BG_NOTES_MAX }), Type.Null()]),
  ),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type BankGuaranteeUpdate = Static<typeof BankGuaranteeUpdateSchema>;
